// src/admin/pages/AdminCoupons.js
import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../contexts/ToastNotification';
import axios from 'axios';
import './admin-coupons.scss';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        type: 'percentage',
        value: '',
        minPurchase: '',
        maxDiscount: '',
        startDate: '',
        endDate: '',
        usageLimit: '',
        usedCount: 0,
        active: true,
        applicableProducts: [],
        applicableCategories: []
    });
    const [filters, setFilters] = useState({ search: '', type: '', active: '' });
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });

    // Tipi di coupon
    const couponTypes = [
        { value: 'percentage', label: '📊 Percentuale (%)', icon: '%' },
        { value: 'fixed', label: '💰 Fisso (€)', icon: '€' },
        { value: 'free_shipping', label: '🚚 Spedizione Gratuita', icon: '📦' }
    ];

    // Carica coupon
    const loadCoupons = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/admin/coupons?page=${pagination.page}&limit=20`;
            if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
            if (filters.type) url += `&type=${filters.type}`;
            if (filters.active !== '') url += `&active=${filters.active}`;
            
            // ✅ Convertito a axios
            const response = await axios.get(url, { withCredentials: true });
            const data = response.data;
            
            if (data.success) {
                setCoupons(data.coupons);
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination.total,
                    totalPages: data.pagination.totalPages
                }));
            } else {
                showToast('Errore nel caricamento dei coupon', 'error');
                // Dati di esempio per sviluppo
                setCoupons([
                    {
                        id: 1,
                        code: 'WELCOME10',
                        type: 'percentage',
                        value: 10,
                        minPurchase: 50,
                        maxDiscount: 100,
                        startDate: '2024-01-01',
                        endDate: '2024-12-31',
                        usageLimit: 100,
                        usedCount: 45,
                        active: true,
                        createdAt: '2024-01-01'
                    },
                    {
                        id: 2,
                        code: 'SHIPFREE',
                        type: 'free_shipping',
                        value: 0,
                        minPurchase: 30,
                        startDate: '2024-01-01',
                        endDate: '2024-12-31',
                        usageLimit: 500,
                        usedCount: 127,
                        active: true,
                        createdAt: '2024-01-01'
                    },
                    {
                        id: 3,
                        code: 'SUMMER20',
                        type: 'percentage',
                        value: 20,
                        minPurchase: 100,
                        maxDiscount: 50,
                        startDate: '2024-06-01',
                        endDate: '2024-08-31',
                        usageLimit: 50,
                        usedCount: 23,
                        active: true,
                        createdAt: '2024-06-01'
                    }
                ]);
            }
        } catch (error) {
            console.error('Error loading coupons:', error);
            showToast('Errore di connessione', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, filters.search, filters.type, filters.active]);

    useEffect(() => {
        loadCoupons();
    }, [loadCoupons]);

    // Resetta form
    const resetForm = () => {
        setFormData({
            code: '',
            type: 'percentage',
            value: '',
            minPurchase: '',
            maxDiscount: '',
            startDate: '',
            endDate: '',
            usageLimit: '',
            usedCount: 0,
            active: true,
            applicableProducts: [],
            applicableCategories: []
        });
        setEditingCoupon(null);
    };

    // Apri modal creazione
    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    // Apri modal modifica
    const openEditModal = (coupon) => {
        setEditingCoupon(coupon);
        setFormData({
            code: coupon.code,
            type: coupon.type,
            value: coupon.value,
            minPurchase: coupon.minPurchase || '',
            maxDiscount: coupon.maxDiscount || '',
            startDate: coupon.startDate?.split('T')[0] || '',
            endDate: coupon.endDate?.split('T')[0] || '',
            usageLimit: coupon.usageLimit || '',
            usedCount: coupon.usedCount || 0,
            active: coupon.active,
            applicableProducts: coupon.applicableProducts || [],
            applicableCategories: coupon.applicableCategories || []
        });
        setShowModal(true);
    };

    // Salva coupon
    const saveCoupon = async () => {
        if (!formData.code) {
            showToast('Inserisci il codice coupon', 'warning');
            return;
        }
        if (!formData.value && formData.type !== 'free_shipping') {
            showToast('Inserisci il valore dello sconto', 'warning');
            return;
        }

        try {
            const url = editingCoupon 
                ? `${API_URL}/admin/coupons/${editingCoupon.id}`
                : `${API_URL}/admin/coupons`;
            
            // ✅ Convertito a axios
            let response;
            if (editingCoupon) {
                response = await axios.put(url, formData, { withCredentials: true });
            } else {
                response = await axios.post(url, formData, { withCredentials: true });
            }
            
            const data = response.data;
            
            if (data.success) {
                showToast(editingCoupon ? 'Coupon aggiornato con successo' : 'Coupon creato con successo', 'success');
                setShowModal(false);
                resetForm();
                loadCoupons();
            } else {
                showToast(data.message || 'Errore nel salvataggio', 'error');
            }
        } catch (error) {
            console.error('Error saving coupon:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    // Elimina coupon
    const deleteCoupon = async (id) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo coupon?')) return;
        
        try {
            // ✅ Convertito a axios
            const response = await axios.delete(`${API_URL}/admin/coupons/${id}`, { withCredentials: true });
            const data = response.data;
            
            if (data.success) {
                showToast('Coupon eliminato con successo', 'success');
                loadCoupons();
            } else {
                showToast(data.message || 'Errore nell\'eliminazione', 'error');
            }
        } catch (error) {
            console.error('Error deleting coupon:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    // Toggle stato coupon
    const toggleCouponStatus = async (id, currentStatus) => {
        try {
            // ✅ Convertito a axios
            const response = await axios.put(
                `${API_URL}/admin/coupons/${id}/toggle`,
                { active: !currentStatus },
                { withCredentials: true }
            );
            
            const data = response.data;
            
            if (data.success) {
                showToast(`Coupon ${!currentStatus ? 'attivato' : 'disattivato'}`, 'success');
                loadCoupons();
            } else {
                showToast(data.message || 'Errore', 'error');
            }
        } catch (error) {
            console.error('Error toggling coupon:', error);
            showToast('Errore di connessione', 'error');
        }
    };

    // Formatta data
    const formatDate = (dateString) => {
        if (!dateString) return 'N/D';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT');
    };

    // Ottieni tipo formattato
    /* const getTypeDisplay = (type, value) => {
        switch(type) {
            case 'percentage': return `${value}%`;
            case 'fixed': return `€ ${value}`;
            case 'free_shipping': return 'Spedizione Gratuita';
            default: return type;
        }
    }; */

    // Genera codice casuale
    const generateRandomCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData({ ...formData, code });
    };

    if (loading && coupons.length === 0) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <span>Caricamento coupon...</span>
            </div>
        );
    }

    return (
        <div className="admin-coupons">
            {/* Header */}
            <div className="coupons-header">
                <div className="header-title">
                    <h2>🏷️ Coupon e Sconti</h2>
                    <p>Gestisci codici sconto, promozioni e offerte speciali</p>
                </div>
                <div className="header-actions">
                    <button className="create-btn" onClick={openCreateModal}>
                        + Nuovo Coupon
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="coupons-filters">
                <div className="search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Cerca per codice..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                        className="search-input"
                    />
                </div>
                <select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value, page: 1 }))}
                    className="filter-select"
                >
                    <option value="">📊 Tutti i tipi</option>
                    {couponTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </select>
                <select
                    value={filters.active}
                    onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value, page: 1 }))}
                    className="filter-select"
                >
                    <option value="">🔄 Tutti gli stati</option>
                    <option value="true">✅ Attivi</option>
                    <option value="false">⛔ Disattivi</option>
                </select>
            </div>

            {/* Stats Cards */}
            <div className="coupons-stats">
                <div className="stat-card">
                    <div className="stat-icon">🏷️</div>
                    <div className="stat-value">{coupons.length}</div>
                    <div className="stat-label">Totale Coupon</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">✅</div>
                    <div className="stat-value">{coupons.filter(c => c.active).length}</div>
                    <div className="stat-label">Attivi</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-value">{coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0)}</div>
                    <div className="stat-label">Totale Utilizzi</div>
                </div>
            </div>

            {/* Coupons Grid */}
            <div className="coupons-grid">
                {coupons.map(coupon => (
                    <div key={coupon.id} className={`coupon-card ${!coupon.active ? 'inactive' : ''}`}>
                        <div className="coupon-header">
                            <div className="coupon-code">{coupon.code}</div>
                            <div className="coupon-actions">
                                <button className="edit-btn" onClick={() => openEditModal(coupon)}>✏️</button>
                                <button className="delete-btn" onClick={() => deleteCoupon(coupon.id)}>🗑️</button>
                            </div>
                        </div>
                        <div className="coupon-body">
                            <div className="coupon-value">
                                {coupon.type === 'percentage' && `${coupon.value}% SCONTO`}
                                {coupon.type === 'fixed' && `€ ${coupon.value} SCONTO`}
                                {coupon.type === 'free_shipping' && '🚚 SPEDIZIONE GRATUITA'}
                            </div>
                            {coupon.minPurchase > 0 && (
                                <div className="coupon-condition">✅ Minimo acquisto: € {coupon.minPurchase}</div>
                            )}
                            {coupon.maxDiscount > 0 && coupon.type === 'percentage' && (
                                <div className="coupon-condition">🎯 Sconto massimo: € {coupon.maxDiscount}</div>
                            )}
                            <div className="coupon-dates">
                                <span>📅 {formatDate(coupon.startDate)} - {formatDate(coupon.endDate)}</span>
                            </div>
                            <div className="coupon-usage">
                                <span>📊 Utilizzato: {coupon.usedCount || 0}</span>
                                {coupon.usageLimit && (
                                    <span> / {coupon.usageLimit}</span>
                                )}
                            </div>
                        </div>
                        <div className="coupon-footer">
                            <button 
                                className={`status-toggle ${coupon.active ? 'active' : 'inactive'}`}
                                onClick={() => toggleCouponStatus(coupon.id, coupon.active)}
                            >
                                {coupon.active ? '✅ Attivo' : '⛔ Disattivo'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* No Results */}
            {coupons.length === 0 && !loading && (
                <div className="no-results">
                    <div className="no-results-icon">🏷️</div>
                    <p>Nessun coupon trovato</p>
                    <button className="create-btn" onClick={openCreateModal}>Crea il primo coupon</button>
                </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                        ← Precedente
                    </button>
                    <span>Pagina {pagination.page} di {pagination.totalPages}</span>
                    <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                        Successiva →
                    </button>
                </div>
            )}

            {/* Modal Creazione/Modifica */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingCoupon ? '✏️ Modifica Coupon' : '➕ Nuovo Coupon'}</h3>
                            <button className="close-modal" onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Codice Coupon *</label>
                                <div className="code-input-group">
                                    <input
                                        type="text"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                        placeholder="es. WELCOME10"
                                        className="code-input"
                                    />
                                    <button type="button" className="generate-btn" onClick={generateRandomCode}>
                                        🎲 Genera
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Tipo di Sconto *</label>
                                <div className="type-buttons">
                                    {couponTypes.map(type => (
                                        <button
                                            key={type.value}
                                            type="button"
                                            className={`type-btn ${formData.type === type.value ? 'active' : ''}`}
                                            onClick={() => setFormData({ ...formData, type: type.value })}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {formData.type !== 'free_shipping' && (
                                <div className="form-group">
                                    <label>Valore Sconto *</label>
                                    <input
                                        type="number"
                                        value={formData.value}
                                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                                        placeholder={formData.type === 'percentage' ? 'es. 10 (percentuale)' : 'es. 10 (euro)'}
                                        className="form-input"
                                    />
                                </div>
                            )}
                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Acquisto minimo (€)</label>
                                    <input
                                        type="number"
                                        value={formData.minPurchase}
                                        onChange={(e) => setFormData({ ...formData, minPurchase: e.target.value })}
                                        placeholder="Nessun minimo"
                                        className="form-input"
                                    />
                                </div>
                                {formData.type === 'percentage' && (
                                    <div className="form-group half">
                                        <label>Sconto massimo (€)</label>
                                        <input
                                            type="number"
                                            value={formData.maxDiscount}
                                            onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                                            placeholder="Nessun limite"
                                            className="form-input"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Data inizio</label>
                                    <input
                                        type="date"
                                        value={formData.startDate}
                                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group half">
                                    <label>Data fine</label>
                                    <input
                                        type="date"
                                        value={formData.endDate}
                                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Limite utilizzi</label>
                                    <input
                                        type="number"
                                        value={formData.usageLimit}
                                        onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                                        placeholder="Illimitato"
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowModal(false)}>Annulla</button>
                            <button className="save-btn" onClick={saveCoupon}>💾 Salva Coupon</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminCoupons;